import type { challengeMetrics } from '@dovefiniscono/shared';
import type { LocalRecord } from '@dovefiniscono/sync';
import { addDays, toISODate } from '../lib/dates';

// Challenges you set yourself: every day or every week, measured by the app or ticked by hand,
// each with its own points. Like the rest of the game they are counted, never stored.

export type Challenge = LocalRecord<'challenges'>;
export type ChallengeMetric = (typeof challengeMetrics)[number];

/** What a day brings to a challenge (a subset of the game's day statistics). */
export type ChallengeDay = {
  minutes: number;
  sessions: number;
  tasks: number;
  onTime: number;
  units: number;
  blocksKept: number;
  recorded: number;
};

export type ChallengeProgress = {
  id: string;
  challengeId: string;
  title: string;
  metric: ChallengeMetric;
  period: 'day' | 'week';
  target: number;
  /** Progress, never past the target. */
  done: number;
  met: boolean;
  points: number;
  /** Ticked by hand today (for the ones you tick yourself). */
  tickedToday: boolean;
};

export const DEFAULT_POINTS = { day: 25, week: 50 } as const;

/** Monday-based weekday: 0 = Monday … 6 = Sunday. */
export const weekdayOf = (day: string) => (new Date(`${day}T12:00:00`).getDay() + 6) % 7;

const lastDay = (challenge: Challenge) =>
  challenge.archivedAt ? toISODate(new Date(challenge.archivedAt)) : null;

/** A daily challenge counts on this day: started, not archived before, one of its weekdays. */
export function appliesOn(challenge: Challenge, day: string): boolean {
  if (challenge.period !== 'day' || day < challenge.since) return false;
  const last = lastDay(challenge);
  if (last !== null && day > last) return false;
  return challenge.weekdays === null || challenge.weekdays.includes(weekdayOf(day));
}

/** A weekly challenge counts for the week starting on this Monday. */
export function appliesWeek(challenge: Challenge, monday: string): boolean {
  if (challenge.period !== 'week' || challenge.since > monday) return false;
  const last = lastDay(challenge);
  return last === null || monday <= last;
}

/** The value of a metric over some days: a sum, or a count of days for ticks and records. */
export function valueOver(
  challenge: Pick<Challenge, 'metric' | 'doneDays'>,
  dayList: readonly string[],
  days: ReadonlyMap<string, ChallengeDay>,
): number {
  if (challenge.metric === 'manual') {
    const ticked = new Set(challenge.doneDays);
    return dayList.filter((day) => ticked.has(day)).length;
  }
  if (challenge.metric === 'recordedDays') {
    return dayList.filter((day) => (days.get(day)?.recorded ?? 0) > 0).length;
  }
  const field: keyof ChallengeDay =
    challenge.metric === 'focusMinutes' ? 'minutes' : challenge.metric;
  return dayList.reduce((sum, day) => sum + (days.get(day)?.[field] ?? 0), 0);
}

const weekOf = (monday: string) => Array.from({ length: 7 }, (_, index) => addDays(monday, index));

function progressOf(
  challenge: Challenge,
  dayList: readonly string[],
  days: ReadonlyMap<string, ChallengeDay>,
  today: string,
): ChallengeProgress {
  const value = valueOver(challenge, dayList, days);
  return {
    id: `mia:${challenge.id}`,
    challengeId: challenge.id,
    title: challenge.title,
    metric: challenge.metric,
    period: challenge.period,
    target: challenge.target,
    done: Math.min(value, challenge.target),
    met: value >= challenge.target,
    points: challenge.points,
    tickedToday: challenge.doneDays.includes(today),
  };
}

/** Your daily challenges for a day. */
export function dailyChallenges(
  challenges: readonly Challenge[],
  days: ReadonlyMap<string, ChallengeDay>,
  day: string,
): ChallengeProgress[] {
  return challenges
    .filter((challenge) => appliesOn(challenge, day))
    .map((challenge) => progressOf(challenge, [day], days, day));
}

/** Your weekly challenges for the week starting on `monday`; `today` says what is ticked now. */
export function weeklyChallenges(
  challenges: readonly Challenge[],
  days: ReadonlyMap<string, ChallengeDay>,
  monday: string,
  today: string,
): ChallengeProgress[] {
  return challenges
    .filter((challenge) => appliesWeek(challenge, monday))
    .map((challenge) => progressOf(challenge, weekOf(monday), days, today));
}

/** Days, then weeks, walked at most this far back: three years. */
const MAX_STEPS = 1100;

/** Points earned so far: each day or week a challenge was met, up to today. */
export function challengePoints(
  challenges: readonly Challenge[],
  days: ReadonlyMap<string, ChallengeDay>,
  today: string,
  startOfWeek: (day: string) => string,
): { points: number; met: number } {
  let points = 0;
  let met = 0;
  for (const challenge of challenges) {
    if (challenge.period === 'day') {
      let day = challenge.since;
      for (let steps = 0; day <= today && steps < MAX_STEPS; day = addDays(day, 1), steps++) {
        if (!appliesOn(challenge, day)) continue;
        if (valueOver(challenge, [day], days) >= challenge.target) {
          points += challenge.points;
          met++;
        }
      }
    } else {
      let monday = startOfWeek(challenge.since);
      for (
        let steps = 0;
        monday <= today && steps < MAX_STEPS;
        monday = addDays(monday, 7), steps++
      ) {
        if (!appliesWeek(challenge, monday)) continue;
        if (valueOver(challenge, weekOf(monday), days) >= challenge.target) {
          points += challenge.points;
          met++;
        }
      }
    }
  }
  return { points, met };
}

/** The ticks of a hand-ticked challenge with today switched on or off. */
export function toggleToday(doneDays: readonly string[], today: string): string[] {
  return doneDays.includes(today)
    ? doneDays.filter((day) => day !== today)
    : [...doneDays, today].sort().slice(-400);
}

/** The words for a measured challenge: "60 minuti di focus", "3 attività completate". */
export function challengeWords(
  metric: ChallengeMetric,
  target: number,
  period: 'day' | 'week',
): string {
  const one = target === 1;
  switch (metric) {
    case 'manual':
      return period === 'week' ? `${target} ${one ? 'volta' : 'volte'} a settimana` : 'Spuntala tu';
    case 'focusMinutes':
      return target % 60 === 0
        ? `${target / 60} ${target === 60 ? 'ora' : 'ore'} di focus`
        : `${target} minuti di focus`;
    case 'sessions':
      return `${target} ${one ? 'sessione' : 'sessioni'} di focus`;
    case 'tasks':
      return `${target} ${one ? 'attività completata' : 'attività completate'}`;
    case 'onTime':
      return `${target} ${one ? 'attività chiusa' : 'attività chiuse'} entro la scadenza`;
    case 'units':
      return `${target} ${one ? 'unità di traguardo' : 'unità di traguardi'}`;
    case 'blocksKept':
      return `${target} ${one ? 'blocco di tempo rispettato' : 'blocchi di tempo rispettati'}`;
    case 'recordedDays':
      return period === 'day'
        ? 'Spese registrate in giornata'
        : `Spese registrate in ${target} ${one ? 'giorno' : 'giorni'}`;
  }
}
